<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import {
	TimeRangeItem,
	TimeRangeValue,
} from "@/modules/rule_engine/ruleEngineClasses";

// Props
const { showSelectionSummary } = defineProps({
	showSelectionSummary: {
		type: Boolean,
		default: false,
	},
});

// Model - now holds both resolution and time ranges
const value = defineModel<{
	timeRanges: TimeRangeValue;
}>();

// Internal state to track individual selected time slots
const selectedTimeSlots = ref([]);

// Emits
const emit = defineEmits(["resolution-changed"]);

// Resolution mapping
const resolutionMapping = {
	hourly: 60,
	"30min": 30,
	"15min": 15,
	"10min": 10,
	"5min": 5,
};

const resolutionOptions = [
	{ label: "Hourly", value: "hourly" },
	{ label: "Every 30 minutes", value: "30min" },
	{ label: "Every 15 minutes", value: "15min" },
	{ label: "Every 10 minutes", value: "10min" },
	{ label: "Every 5 minutes", value: "5min" },
];

// Computed
const selectedResolutionMinutes = computed(() => {
	return resolutionMapping[value.value.timeRanges.resolution];
});

const availableTimes = computed(() => {
	const times = [];
	const totalMinutes = 24 * 60; // 24 hours in minutes
	const resolution = selectedResolutionMinutes.value;

	for (let minutes = 0; minutes < totalMinutes; minutes += resolution) {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		const timeString = `${hours.toString().padStart(2, "0")}:${mins
			.toString()
			.padStart(2, "0")}`;
		times.push(timeString);
	}

	return times;
});

// Helper functions
const timeToMinutes = (timeString) => {
	const [hours, minutes] = timeString.split(":").map(Number);
	return hours * 60 + minutes;
};

const minutesToTime = (minutes) => {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return `${hours.toString().padStart(2, "0")}:${mins
		.toString()
		.padStart(2, "0")}`;
};

const consolidateTimeSlots = (timeSlots): TimeRangeItem[] => {
	if (timeSlots.length === 0) return [];

	// Sort time slots by their time value
	const sortedSlots = sortTimes([...timeSlots]);
	const ranges = [];
	let currentStart = sortedSlots[0];
	let currentEnd = null;

	for (let i = 0; i < sortedSlots.length; i++) {
		const currentTime = sortedSlots[i];
		const currentMinutes = timeToMinutes(currentTime);
		const nextTime = sortedSlots[i + 1];

		// Calculate the end time for this slot
		const endMinutes = currentMinutes + selectedResolutionMinutes.value;
		currentEnd = minutesToTime(endMinutes);

		// Check if next slot is consecutive
		if (nextTime && timeToMinutes(nextTime) === endMinutes) {
			// Continue the current range
			continue;
		} else {
			// End the current range
			ranges.push({
				start: currentStart,
				end: currentEnd,
			});

			// Start a new range if there's a next slot
			if (nextTime) {
				currentStart = nextTime;
			}
		}
	}

	return ranges;
};

const updateModel = () => {
	value.value = {
		timeRanges: {
			...value.value.timeRanges,
			times: consolidateTimeSlots(selectedTimeSlots.value),
		},
	};
};

// Methods
const sortTimes = (times) => {
	return times.sort((a, b) => {
		const [aHours, aMinutes] = a.split(":").map(Number);
		const [bHours, bMinutes] = b.split(":").map(Number);
		return aHours * 60 + aMinutes - (bHours * 60 + bMinutes);
	});
};

const toggleTime = (time) => {
	const index = selectedTimeSlots.value.indexOf(time);
	if (index > -1) {
		selectedTimeSlots.value.splice(index, 1);
	} else {
		selectedTimeSlots.value.push(time);
		// Sort the selected times
		selectedTimeSlots.value = sortTimes([...selectedTimeSlots.value]);
	}
	updateModel();
};

const removeTime = (time) => {
	const index = selectedTimeSlots.value.indexOf(time);
	if (index > -1) {
		selectedTimeSlots.value.splice(index, 1);
	}
	updateModel();
};

const clearSelection = () => {
	selectedTimeSlots.value = [];
	updateModel();
};

const selectAll = () => {
	selectedTimeSlots.value = [...availableTimes.value];
	updateModel();
};

const onResolutionChange = (newResolution) => {
	const previousResolutionMinutes = selectedResolutionMinutes.value;
	const newResolutionMinutes = resolutionMapping[newResolution];

	// Convert existing selections to new resolution
	const expandedTimes = new Set();

	selectedTimeSlots.value.forEach((selectedTime) => {
		const [hours, minutes] = selectedTime.split(":").map(Number);
		const selectedMinutes = hours * 60 + minutes;

		// If going to a higher resolution (smaller interval), expand the selection
		if (newResolutionMinutes < previousResolutionMinutes) {
			// Generate all sub-intervals within the original time slot
			for (
				let offset = 0;
				offset < previousResolutionMinutes;
				offset += newResolutionMinutes
			) {
				const newTimeMinutes = selectedMinutes + offset;
				if (newTimeMinutes < 24 * 60) {
					// Don't exceed 24 hours
					const newHours = Math.floor(newTimeMinutes / 60);
					const newMins = newTimeMinutes % 60;
					const newTimeString = `${newHours
						.toString()
						.padStart(2, "0")}:${newMins.toString().padStart(2, "0")}`;
					expandedTimes.add(newTimeString);
				}
			}
		} else {
			// If going to a lower resolution (larger interval), keep only the start time if it aligns
			if (selectedMinutes % newResolutionMinutes === 0) {
				expandedTimes.add(selectedTime);
			}
		}
	});

	selectedTimeSlots.value = sortTimes(Array.from(expandedTimes));

	// Update the model with new resolution and converted times
	value.value = {
		timeRanges: {
			resolution: newResolution,
			times: consolidateTimeSlots(selectedTimeSlots.value),
		},
	};

	emit("resolution-changed", newResolution);
};

onMounted(() => {
	selectedTimeSlots.value = availableTimes.value.filter((time) => {
		const tm = timeToMinutes(time);
		return value.value.timeRanges.times.some(
			(timeRange) =>
				tm >= timeToMinutes(timeRange.start) &&
				tm < timeToMinutes(timeRange.end)
		);
	});
});
</script>

<template>
	<div>
		<!-- Resolution Selector -->
		<v-row class="mb-2">
			<v-col cols="12" md="6">
				<v-select
					:model-value="value.timeRanges.resolution"
					:items="resolutionOptions"
					label="Time Resolution"
					item-title="label"
					item-value="value"
					@update:model-value="onResolutionChange"
					variant="outlined"
					density="compact"
					hide-details
				/>
			</v-col>
		</v-row>

		<!-- Time Grid -->
		<div class="time-grid-container pa-3">
			<div class="mb-2 text-subtitle-2">
				Select Time Slots ({{ selectedTimeSlots.length }} selected)
			</div>

			<div class="time-grid">
				<v-chip
					v-for="time in availableTimes"
					:key="time"
					:color="selectedTimeSlots.includes(time) ? 'primary' : 'default'"
					:variant="selectedTimeSlots.includes(time) ? 'flat' : 'outlined'"
					class="ma-1 time-chip"
					clickable
					@click="toggleTime(time)"
				>
					{{ time }}
				</v-chip>
			</div>
		</div>

		<!-- Selection Summary -->
		<div
			class="selection-summary mt-4 pa-3"
			v-if="showSelectionSummary && selectedTimeSlots.length > 0"
		>
			<div class="mb-2 text-subtitle-2">Selected Times</div>
			<div class="selected-times">
				<v-chip
					v-for="time in selectedTimeSlots"
					:key="time"
					color="primary"
					variant="flat"
					closable
					class="ma-1"
					@click:close="removeTime(time)"
				>
					{{ time }}
				</v-chip>
			</div>
		</div>

		<!-- Action Buttons -->
		<v-row class="mt-4">
			<v-col>
				<v-btn
					color="error"
					variant="text"
					@click="clearSelection"
					:disabled="selectedTimeSlots.length === 0"
				>
					Clear All
				</v-btn>
				<v-btn color="primary" variant="tonal" class="ml-2" @click="selectAll">
					Select All
				</v-btn>
			</v-col>
		</v-row>
	</div>
</template>

<style scoped>
.time-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
	gap: 8px;
	max-height: calc(
		3 * (32px + 16px) + 16px
	); /* 3 chip rows + 2 gaps between rows + padding */
	overflow-y: auto;
	padding: 8px;
	border-radius: 4px;
	background-color: #fafafa;
}

.time-chip {
	width: 80px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: "Roboto Mono", monospace;
	font-size: 0.875rem;
	flex: none !important;
}

.selected-times {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}

.time-grid-container {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	background-color: #fafafa;
}

.selection-summary {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	background-color: #fafafa;
}

.time-grid::-webkit-scrollbar {
	width: 6px;
}

.time-grid::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 3px;
}

.time-grid::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 3px;
}

.time-grid::-webkit-scrollbar-thumb:hover {
	background: #a1a1a1;
}
</style>
